import { db, storage } from '../firebase/config';
import { doc, deleteDoc, setDoc, serverTimestamp, collection, query, where, getDocs, getDoc, addDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { Form, FormPage, FormElement, FormResponse } from '../types/formTypes';
import { orderBy, limit, startAfter, Timestamp } from 'firebase/firestore';

/**
 * Converts Firestore timestamps in document data to JavaScript Date objects or timestamps
 * @param data - The document data that might contain timestamp objects
 * @returns The data with timestamps converted to numbers (milliseconds since epoch)
 */
export const convertFirestoreTimestamps = (data: any): any => {
  if (data instanceof Timestamp) {
    return data.toMillis();
  }
  if (Array.isArray(data)) {
    return data.map(convertFirestoreTimestamps);
  }
  if (data !== null && typeof data === 'object' && !(data instanceof Date)) {
    const newData: { [key: string]: any } = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        newData[key] = convertFirestoreTimestamps(data[key]);
      }
    }
    return newData;
  }
  return data;
};

/**
 * Creates or updates a form in Firestore
 * @param form - The form to create or update in Firestore
 * @returns Promise that resolves to the created/updated form with UI-friendly timestamps
 */
export const createFormInFirestore = async (form: Form): Promise<Form> => {
  try {
    //console.log('Creating/updating form in Firestore:', form.id);
    
    // Create Firestore document reference
    const formRef = doc(db, 'forms', form.id);
    
    // Create a copy for Firestore that uses server timestamp for updatedAt
    const firestoreData = {
      ...form,
      // Use server timestamp for Firestore storage
      updatedAt: serverTimestamp(),
    };
    
    // Clean up the data by removing any undefined values 
    // that would cause errors in Firestore
    const cleanData = JSON.parse(JSON.stringify(firestoreData));
    
    // Save to Firestore
    await setDoc(formRef, cleanData, { merge: true });
    
    //console.log('Form successfully saved to Firestore:', form.id);
    
    // For the UI, return a form with a JavaScript timestamp that we can display immediately
    return {
      ...form,
      updatedAt: Date.now(), // Use JavaScript timestamp for immediate UI display
    };
  } catch (error) {
    console.error('Error creating/updating form in Firestore:', error);
    
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    
    throw error;
  }
};

/**
 * Deletes a form from Firestore
 * @param formId - The ID of the form to delete
 * @returns Promise that resolves to true if deletion was successful
 */
export const deleteFormFromFirestore = async (formId: string): Promise<boolean> => {
  try {
    //console.log('Deleting form from Firestore:', formId);
    
    // Create a reference to the form document
    const formRef = doc(db, 'forms', formId);
    
    // Delete the document
    await deleteDoc(formRef);
    
    //console.log('Form successfully deleted from Firestore:', formId);
    return true;
  } catch (error) {
    console.error('Error deleting form from Firestore:', error);
    
    // Check for specific error types
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }

    throw error;
  }
};

/**
 * Saves a form to Firestore
 * @param form - The form to save to Firestore
 * @returns Promise that resolves to the saved form with UI-friendly timestamps
 */
export const saveFormToFirestore = async (form: Form): Promise<{ success: boolean; formId: string }> => {
  let formToSave = { ...form }; // Create a copy
  //console.log('saveFormToFirestore called with form ID:', formToSave.id);

  try {
    if (!formToSave.userId) {
      throw new Error('User ID is missing from the form data.');
    }

    // Prepare data for Firestore
    const dataToSave: any = {
        ...formToSave,
        createdAt: formToSave.createdAt && typeof formToSave.createdAt === 'object' && 'seconds' in formToSave.createdAt && 'nanoseconds' in formToSave.createdAt 
                   ? formToSave.createdAt // It's already a Firestore Timestamp
                   : (typeof formToSave.createdAt === 'number'
                      ? Timestamp.fromMillis(formToSave.createdAt) // Convert from milliseconds
                      : serverTimestamp()), // New form or invalid type, use server timestamp
        updatedAt: serverTimestamp(), // Always use server timestamp for update
    };
    
    // Remove potentially problematic fields before saving
    delete dataToSave.settings?.preserveSettings; // Don't save this transient flag
    delete dataToSave.lastAccessed; // Remove lastAccessed if it exists from old structure
    delete dataToSave.currentPageId; // Remove currentPageId if it exists

    // Remove any fields explicitly set to undefined, as Firestore doesn't support them
    Object.keys(dataToSave).forEach(key => {
      if (dataToSave[key] === undefined) {
        delete dataToSave[key];
      }
    });

    let docRef;
    if (dataToSave.id) {
      // Update existing form
      //console.log(`Updating existing form: ${dataToSave.id}`);
      docRef = doc(db, 'forms', dataToSave.id);
      await setDoc(docRef, dataToSave, { merge: true }); // Use setDoc with merge:true to update or create
    } else {
      // Create new form
      //console.log('Creating new form for user:', dataToSave.userId);
      const formsCollection = collection(db, 'forms');
      
      // Ensure id is removed before adding, let Firestore generate it
      const { id, ...dataWithoutId } = dataToSave; 

      // Add document first to get an ID
      docRef = await addDoc(formsCollection, dataWithoutId); 
      
      // Update the document with its own ID
      await updateDoc(docRef, { id: docRef.id }); 
      formToSave.id = docRef.id; // Update the local form object ID
      //console.log('New form created with ID:', formToSave.id);
    }
    return { success: true, formId: formToSave.id };
  } catch (error) {
    console.error("Error saving form to Firestore:", error);
    // Add more specific error logging if possible
    if (error instanceof Error) {
        console.error("Error details:", error.message, error.stack);
    }
    return { success: false, formId: formToSave.id || '' };
  }
};

/**
 * Adds an image URL to a user's uploaded images collection in Firestore.
 * @param userId The ID of the user.
 * @param imageUrl The public download URL of the uploaded image.
 * @returns Promise resolving when the operation is complete.
 */
export const addUserImage = async (userId: string, imageUrl: string, storagePath: string): Promise<void> => {
  if (!userId || !imageUrl || !storagePath) {
    throw new Error("User ID, Image URL, and Storage Path are required.");
  }
  try {
    const imagesCollectionRef = collection(db, 'users', userId, 'uploadedImages');
    await addDoc(imagesCollectionRef, {
      url: imageUrl,
      path: storagePath, // Store the path for potential deletion
      uploadedAt: serverTimestamp()
    });
    //console.log(`Image metadata added for user ${userId}`);
  } catch (error) {
    console.error("Error adding user image metadata to Firestore:", error);
    throw error;
  }
};

/**
 * Fetches the list of uploaded image URLs for a specific user.
 * @param userId The ID of the user.
 * @returns Promise resolving with an array of image URLs.
 */
interface UserImage {
  id: string;
  url: string;
  path: string; // Include path for deletion
}

export const getUserImages = async (userId: string): Promise<UserImage[]> => {
  if (!userId) {
    console.error("User ID is required to get user images.");
    return [];
  }
  try {
    const imagesCollectionRef = collection(db, 'users', userId, 'uploadedImages');
    const q = query(imagesCollectionRef, orderBy('uploadedAt', 'desc'), limit(50)); 
    const querySnapshot = await getDocs(q);
    
    const userImages: UserImage[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.url && data.path) {
        userImages.push({ 
          id: doc.id, // Include document ID if needed later
          url: data.url, 
          path: data.path 
        });
      }
    });
    //console.log(`Fetched ${userImages.length} images for user ${userId}`);
    return userImages;
  } catch (error) {
    console.error("Error fetching user images from Firestore:", error);
    return [];
  }
};

/**
 * Fetches responses for a form from Firestore
 * @param formId The ID of the form to fetch responses for
 * @returns Promise that resolves to an array of form responses
 */
export const getFormResponses = async (formId: string): Promise<FormResponse[]> => {
  if (!formId) {
    console.error('Form ID is required to get responses.');
    return [];
  }
  try {
    const responsesCollectionRef = collection(db, 'forms', formId, 'responses');
    const q = query(responsesCollectionRef, orderBy('metadata.submittedAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const responses: FormResponse[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Convert timestamp before adding to array
      const submittedAt = data.metadata?.submittedAt instanceof Timestamp 
                          ? data.metadata.submittedAt.toDate().toISOString()
                          : new Date().toISOString(); // Fallback if timestamp is missing/invalid
                          
      responses.push({ 
        id: doc.id, 
        ...data,
        submittedAt: submittedAt // Ensure submittedAt is a string date
      } as FormResponse);
    });
    return responses;
  } catch (error) {
    console.error("Error fetching form responses:", error);
    return [];
  }
};

// --- Potentially add deleteUserImage function here later ---
// export const deleteUserImage = async (userId: string, imageId: string, storagePath: string): Promise<void> => {
//   if (!userId || !imageId || !storagePath) {
//     throw new Error("User ID, Image ID, and Storage Path are required for deletion.");
//   }
//   try {
//     // Delete Firestore document
//     const imageDocRef = doc(db, 'users', userId, 'uploadedImages', imageId);
//     await deleteDoc(imageDocRef);
//     
//     // Delete file from Storage
//     const storageRef = ref(storage, storagePath);
//     await deleteObject(storageRef);
//     
//     console.log(`Successfully deleted image ${imageId} and its file ${storagePath}`);
//   } catch (error) {
//     console.error("Error deleting user image:", error);
//     // Decide how to handle partial failures (e.g., Firestore deleted but Storage failed)
//     throw error;
//   }
// }; 